import { NgModule } from "@angular/core";
import { RouterModule } from "@angular/router";
import { NextPageRoute } from "@cvx/nextpage";
import { RolesPrivilegesComponent } from "./admin/roles-privileges/roles-privileges.component";
import { CustomErrorComponent } from "./shared/error-page/custom-error-page.component";

import { RoleGuard } from "./role-guard";
import { AuthPageConfig } from "./auth-page-config";
import { NoAccessComponent } from "./shared/no-access/no-access.component";
import { CalGuardService } from "@cvx/cal-angular";

import { ApplicationsComponent } from "./applications/applications.component";
import { AssessmentDetailComponent } from "./assessments/assessment-detail.component";
import { AIConfigComponent } from "./admin/ai-config/ai-config.component";
import { AiChatSettingsComponent } from "./admin/ai-chat-settings/ai-chat-settings.component";
import { AiConnectionsComponent } from "./admin/ai-connections/ai-connections.component";
import { AiCmdbAssessmentComponent } from "./admin/ai-cmdb-assessment/ai-cmdb-assessment.component";
import { CmdbSyncComponent } from "./admin/cmdb-sync/cmdb-sync.component";
import { SyncItemLogsComponent } from "./admin/cmdb-sync/sync-item-logs.component";
import { CmdbManagementComponent } from "./admin/cmdb-management/cmdb-management.component";
import { ModernizationReadinessComponent } from "./admin/modernization-readiness/modernization-readiness.component";
import { JobLogsComponent } from "./admin/modernization-readiness/job-logs.component";
import { AiAssessmentJobDetailComponent } from "./admin/ai-assessment/ai-assessment-job-detail/ai-assessment-job-detail.component";
import { UsersSyncComponent } from "./admin/users-sync/users-sync.component";
import { UsersListComponent } from "./admin/users-list/users-list.component";
import { PodsListComponent } from "./admin/pods-list/pods-list.component";
import { ApplicationComponent } from "./admin/application/application.component";
import { PlatformMasterComponent } from "./admin/platformmaster/platform-master.component";
import { LogoutComponent } from "./logout/logout.component";
import { DashboardComponent } from "./dashboard/dashboard.component";
import { AiStatisticsComponent } from "./admin/ai-statistics/ai-statistics.component";
import { UserGuideComponent } from "./user-guide/user-guide.component";
import { ConflictCandidatesPageComponent } from "./dashboard/conflict-candidates-page/conflict-candidates-page.component";
import { PersonalAccessTokensComponent } from "./personal-access-tokens/personal-access-tokens.component";

// Full router routes (includes param routes). New mock routes deliberately do NOT set data.pageName/subPageName
// to avoid privilege-based filtering during the pixel-mock phase.
let routerRoutes: NextPageRoute[] = [
  {
    path: "",
    title: "Main",
    children: [
      {
        path: "",
        redirectTo: "dashboard",
        pathMatch: "full",
      },
      {
        path: "dashboard",
        title: "Dashboard",
        component: DashboardComponent,
        canActivate: [CalGuardService],
      },
      {
        path: "logout",
        title: "Logout",
        component: LogoutComponent,
      },
      {
        path: "applications",
        title: "Application Portfolio",
        component: ApplicationsComponent,
        canActivate: [CalGuardService],
        
      },
      {
        path: "assessment/:id",
        title: "Assessment Detail",
        component: AssessmentDetailComponent,
        canActivate: [CalGuardService],
      },
      {
        path: "code-validation-candidates",
        title: "Code Validation Candidates",
        component: ConflictCandidatesPageComponent,
        canActivate: [CalGuardService],
      },
      {
        path: "user-guide",
        title: "User Guide",
        component: UserGuideComponent,
        canActivate: [CalGuardService],
      },
      {
        path: "pat-setup",
        title: "PAT Setup",
        component: PersonalAccessTokensComponent,
        canActivate: [CalGuardService],
      },
      {
        path: "profile",
        title: "Profile",
        canActivate: [CalGuardService],
        children: [
          {
            path: "",
            redirectTo: "personal-access-tokens",
            pathMatch: "full",
          },
          {
            path: "user-guide",
            redirectTo: "/user-guide",
            pathMatch: "full",
          },
          {
            path: "personal-access-tokens",
            redirectTo: "/pat-setup",
            pathMatch: "full",
          },
        ],
      },
      {
        path: "admin",
        title: "Admin",
        canActivate: [CalGuardService, RoleGuard],
        data: AuthPageConfig.Admin.data,
        children: [
          {
            path: "",
            redirectTo: "application",
            pathMatch: "full",
          },
          {
            path: "application",
            title: "Application",
            component: ApplicationComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminApplication.data,
          },
          {
            path: "ai-connections",
            title: "AI Config - Connections",
            component: AiConnectionsComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminAIConfig.data,
          },
          {
            path: "ai-chat-settings",
            title: "AI Config - Chat Settings",
            component: AiChatSettingsComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminAIConfig.data,
          },
          {
            path: "ai-config",
            title: "AI Config - Code Assessment",
            component: AIConfigComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminAIConfig.data,
          },
          {
            path: "ai-cmdb-assessment",
            title: "AI Config - CMDB Assessment",
            component: AiCmdbAssessmentComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminAIConfig.data,
          },
          {
            path: "ai-statistics",
            title: "AI Statistics",
            component: AiStatisticsComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminAIConfig.data,
          },
          {
            path: "cmdb-sync",
            title: "CMDB Management",
            component: CmdbManagementComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminCmdbSync.data,
          },
          {
            path: "users-sync",
            title: "Users Sync",
            component: UsersSyncComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminCmdbSync.data, // Reuse same admin permission for now
          },
          {
            path: "users-list",
            title: "User List",
            component: UsersListComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminCmdbSync.data, // Reuse same admin permission for now
          },
          {
            path: "pods-list",
            title: "Application",
            component: ApplicationComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminApplication.data,
          },
          {
            path: "platform-master",
            title: "Platform Master",
            component: PlatformMasterComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminPlatformMaster.data,
          },
          {
            path: "cmdb-sync/history/:id",
            title: "Sync Run Logs",
            component: SyncItemLogsComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminCmdbSync.data,
          },
          {
            path: "modernization-readiness/job/:id",
            title: "Job Logs",
            component: JobLogsComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminCmdbSync.data, // Reuse same admin permission for now
          },
          {
            path: "ai-assessment",
            redirectTo: "ai-config",
            pathMatch: "full"
          },
          {
            path: "ai-assessment/job/:id",
            title: "AI Assessment Job Detail",
            component: AiAssessmentJobDetailComponent,
            canActivate: [CalGuardService],
            data: AuthPageConfig.AdminAIConfig.data,
          },
          {
            path: "roles-privileges",
            title: "Roles & Privileges",
            component: RolesPrivilegesComponent,
            canActivate: [CalGuardService, RoleGuard],
            data: AuthPageConfig.AdminRolesPrivileges.data,
          },
        ],
      },
    ],
  },
  {
    path: "home",
    title: "Home",
    redirectTo: "/",
    pathMatch: "full",
  },
  { path: "error", component: CustomErrorComponent },
  { path: "no-access", component: NoAccessComponent },
];

// Menu routes (top-level only): Dashboard is visible to all signed-in users,
// while Admin is shown only when the user's permissions allow it.
// This intentionally excludes param routes like /assess/:appId and /assessment/:id.
// NOTE: np-menu appears to build navigation from top-level route entries, so we keep these as top-level.
// Authorization is based solely on requiredPermissionKey - permissions are derived from user roles.
export let menuRoutes: NextPageRoute[] = [
  {
    path: "dashboard",
    title: "Dashboard",
    component: DashboardComponent,
    canActivate: [CalGuardService],
  },
  {
    path: "admin",
    title: "Admin",
    canActivate: [CalGuardService, RoleGuard],
    data: AuthPageConfig.Admin.data,
    children: [
      {
        path: "",
        redirectTo: "application",
        pathMatch: "full",
      },
      {
        path: "application",
        title: "Application",
        component: ApplicationComponent,
        canActivate: [CalGuardService, RoleGuard],
        data: AuthPageConfig.AdminApplication.data,
      },
      {
        path: "ai-connections",
        title: "AI Config - Connections",
        component: AiConnectionsComponent,
        canActivate: [CalGuardService, RoleGuard],
        data: AuthPageConfig.AdminAIConfig.data,
      },
      {
        path: "ai-chat-settings",
        title: "AI Config - Chat Settings",
        component: AiChatSettingsComponent,
        canActivate: [CalGuardService, RoleGuard],
        data: AuthPageConfig.AdminAIConfig.data,
      },
      {
        path: "ai-config",
        title: "AI Config - Code Assessment",
        component: AIConfigComponent,
        canActivate: [CalGuardService, RoleGuard],
        data: AuthPageConfig.AdminAIConfig.data,
      },
      {
        path: "ai-cmdb-assessment",
        title: "AI Config - CMDB Assessment",
        component: AiCmdbAssessmentComponent,
        canActivate: [CalGuardService, RoleGuard],
        data: AuthPageConfig.AdminAIConfig.data,
      },
      {
        path: "ai-statistics",
        title: "AI Statistics",
        component: AiStatisticsComponent,
        canActivate: [CalGuardService, RoleGuard],
        data: AuthPageConfig.AdminAIConfig.data,
      },
      {
        path: "cmdb-sync",
        title: "CMDB Management",
        component: CmdbManagementComponent,
        canActivate: [CalGuardService],
        data: AuthPageConfig.AdminCmdbSync.data,
      },
      {
        path: "users-sync",
        title: "Users Sync",
        component: UsersSyncComponent,
        canActivate: [CalGuardService],
        data: AuthPageConfig.AdminCmdbSync.data, // Reuse same admin permission for now
      },
      {
        path: "users-list",
        title: "User List",
        component: UsersListComponent,
        canActivate: [CalGuardService],
        data: AuthPageConfig.AdminCmdbSync.data, // Reuse same admin permission for now
      },
      {
        path: "platform-master",
        title: "Platform Master",
        component: PlatformMasterComponent,
        canActivate: [CalGuardService],
        data: AuthPageConfig.AdminCmdbSync.data, // Reuse same admin permission for now
      },
    ],
  },
  {
    path: "user-guide",
    title: "User Guide",
    component: UserGuideComponent,
    canActivate: [CalGuardService],
  },
  {
    path: "pat-setup",
    title: "PAT Setup",
    component: PersonalAccessTokensComponent,
    canActivate: [CalGuardService],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routerRoutes)],
  exports: [RouterModule],
})
export class AppRoutingModule {
  // Route filtering moved to app.component after user context initialization
}

